import React, { useEffect, useState, useCallback } from 'react';
import { Table, Tag, Select, DatePicker, Button, Typography, Row, Col } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '../api/client';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  dispensed: 'default',
  expired: 'red',
  cancelled: 'volcano',
  partially_dispensed: 'blue',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Активен',
  dispensed: 'Выдан',
  expired: 'Истёк',
  cancelled: 'Отменён',
  partially_dispensed: 'Частично',
};

interface Rx {
  id: string;
  drug_name: string;
  dosage: string;
  quantity: number;
  status: string;
  patient_name: string | null;
  patient_iin: string | null;
  valid_until: string;
  created_at: string;
  dispensed_at: string | null;
}

const LIMIT = 20;

function extractList(data: unknown): Rx[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.items)) return obj.items as Rx[];
    if (Array.isArray(obj.data)) return obj.data as Rx[];
  }
  return [];
}

function extractTotal(data: unknown): number | undefined {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    if (typeof obj.total === 'number') return obj.total;
    if (typeof obj.count === 'number') return obj.count;
  }
  return undefined;
}

export default function Prescriptions() {
  const [list, setList] = useState<Rx[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: p, limit: LIMIT };
      if (statusFilter) params.status = statusFilter;
      if (dateRange) {
        params.date_from = dateRange[0].toISOString();
        params.date_to = dateRange[1].toISOString();
      }
      const { data } = await api.get('/admin/prescriptions', { params });
      const items = extractList(data);
      setList(items);

      const backendTotal = extractTotal(data);
      if (backendTotal !== undefined) {
        setTotal(backendTotal);
      } else {
        setTotal(items.length < LIMIT ? (p - 1) * LIMIT + items.length : p * LIMIT + 1);
      }
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dateRange]);

  useEffect(() => { load(1); setPage(1); }, [statusFilter, dateRange]);
  useEffect(() => { load(page); }, [page]);

  const columns: ColumnsType<Rx> = [
    {
      title: 'Препарат',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.drug_name}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>{r.dosage} · {r.quantity} уп.</div>
        </div>
      ),
    },
    {
      title: 'Пациент',
      render: (_, r) => (
        <div>
          <div>{r.patient_name ?? '—'}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>ИИН: {r.patient_iin ?? '—'}</div>
        </div>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (s) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{STATUS_LABEL[s] ?? s}</Tag>,
    },
    {
      title: 'Действителен до',
      dataIndex: 'valid_until',
      render: (v) => new Date(v).toLocaleDateString('ru-RU'),
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      render: (v) => new Date(v).toLocaleDateString('ru-RU'),
    },
    {
      title: 'Выдан',
      dataIndex: 'dispensed_at',
      render: (v) => v ? new Date(v).toLocaleDateString('ru-RU') : '—',
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={4} style={{ margin: 0 }}>Все рецепты</Title>
        <Button icon={<ReloadOutlined />} onClick={() => load(page)}>Обновить</Button>
      </div>

      <Row gutter={[12, 8]} className="page-filters" style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder="Статус"
            allowClear
            style={{ width: '100%' }}
            onChange={(v) => setStatusFilter(v)}
            options={Object.entries(STATUS_LABEL).map(([k, v]) => ({ value: k, label: v }))}
          />
        </Col>
        <Col xs={24} sm={12} md={10} lg={8}>
          <RangePicker
            style={{ width: '100%' }}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            placeholder={['От', 'До']}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Button block onClick={() => { setStatusFilter(undefined); setDateRange(null); }}>
            Сбросить
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={list}
        rowKey="id"
        loading={loading}
        bordered
        scroll={{ x: 800 }}
        pagination={{
          current: page,
          pageSize: LIMIT,
          total,
          onChange: setPage,
          showTotal: (t) => `Всего ${t}`,
          responsive: true,
        }}
        locale={{ emptyText: 'Рецептов нет' }}
      />
    </div>
  );
}
